import _ from 'lodash';
import {keys} from 'ts-transformer-keys';

import {FilterService} from 'primereact/api';

import {
    __,
    Placeholders
} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import Item, {
    ItemAttributes,
    ItemAttributesSettable,
    ItemOnlyAttributesSettable
} from '~/shared/src/ci/Item';
import Reference, {
    ReferenceAttributes,
    ReferenceAttributesSettable,
    ReferenceOnlyAttributesSettable
} from '~/shared/src/ci/Reference';
import LocalizableError from '~/shared/src/base/LocalizableError';
import dataUtilities, {
    ItemsAndReferencesCollection
} from '~/shared/src/utilities/dataUtils';
import {
    GetReferenceIdsParameters,
    GetReferenceIdsResult
} from '~/shared/src/utilities/modules/data';
import {
    Condition,
    ConditionOperator,
    DataDescriptorValues
} from '~/shared/src/utilities/condition';
import dataUtils from '~/shared/src/utilities/dataUtils';
import messageUtils from '~/shared/src/utilities/messageUtils';

import {store} from '~/frontend/src/store';
import xhr from '~/frontend/src/utilities/xhr';
import {
    buildDataDescriptor,
    DataDescriptor,
    DataStructure,
    ItemDataDescriptor,
    ReferenceDataDescriptor,
    ReferencePropertyDataDescriptor
} from '~/shared/src/utilities/dataStructure';
import {LoadMetaDataRowData} from '~/shared/src/utilities/modules/overview';
import Property, {PropertyAttributesSettable} from '~/shared/src/ci/Property';
import ReferenceProperty, {ReferencePropertyAttributesSettable} from '~/shared/src/ci/ReferenceProperty';
import {
    getItemTypeDisplayName,
    getPropertySetDisplayName,
    getPropertyTypeDisplayName,
    getReferencePropertySetDisplayName,
    getReferenceSetDisplayName,
    getReferenceTypeDisplayName
} from '~/frontend/src/utilities/renderHelpers';
import {
    statusRenderer
} from '~/frontend/src/utilities/valueRenderers';
import {FilterValueType} from '~/shared/src/validationSchemes/modules/overview';

export type MergeMode = 'override' | 'keepExisting' | 'keepLoaded';

export type LoadedData = {
    items : ( ItemAttributes & Required<Pick<ItemAttributes, 'id'>> )[];
    references : ( ReferenceAttributes & Required<Pick<ReferenceAttributes, 'id'>> )[];
};
type DataList = {
    items : number[];
    references : number[];
};

const loadedDataKeys = keys<LoadedData>() as ( keyof LoadedData )[];

/**
 * Create Item and Reference objects from ajax response and return lists with their ids
 * @param data
 * @param dataContext
 * @param schema
 * @param [mergeMode='override']
 */
export function processData( data : LoadedData, dataContext : string, schema : Schema, mergeMode : MergeMode = 'override' ) : DataList {
    const retVal = {
        items     : [] as number[],
        references: [] as number[]
    };

    for ( const dataKey of loadedDataKeys ) {
        if ( data[ dataKey ] ) {
            for ( const objectData of data[ dataKey ] ) {
                const existingObject = global.data[ dataContext ]?.[ dataKey ]?.[ objectData.id ];

                try {
                    // not existing or override -> create in global.data
                    if ( !existingObject || mergeMode === 'override' ) {
                        new ( dataKey === 'items' ? Item : Reference )( objectData as ItemAttributes & ReferenceAttributes, dataContext, schema );
                    }

                    else {
                        const newObject : Item | Reference = new ( dataKey === 'items' ? Item : Reference )( objectData as ItemAttributes & ReferenceAttributes, dataContext, schema, true ),
                              newObjectAttributes          = newObject.toUpdateJSON( 'all' ) as ItemAttributes | ReferenceAttributes;

                        // have to use this "quirk" to prevent additionally loaded properties not dirty!
                        // TODO: find a proper way in item.clone method to handle this case
                        for ( const propertyTypeId of Object.keys( newObjectAttributes.properties ?? {} ) ) {
                            newObjectAttributes.properties![ propertyTypeId ] = {
                                ...newObjectAttributes.properties![ propertyTypeId ],
                                id: newObject.properties.get( propertyTypeId * 1 )?.id
                            }
                        }

                        // TODO: implement proper handling
                        //  keepExisting (-> keepChanges) => no modified data is overridden
                        //  keepLoaded (-> resetChanges) => all data is overridden
                        existingObject.clone( newObjectAttributes as ItemAttributesSettable | ReferenceAttributesSettable, mergeMode === 'keepLoaded' );
                    }
                }
                catch ( e ) {
                    console.error( e );
                    // TODO: discuss
                    // throws e.g. "still exists" error we don't care here
                }

                // items and references received from backend are set to version 1
                // TODO: check why we don't use the { id: versioCount } notation directly here. This will be converted in actions/data/loadData later...)
                retVal[ dataKey ].push( objectData.id );
            }
        }
    }

    return retVal;
}

/**
 * Is data in global.data modified.
 *
 * @param {string} dataContext
 * @param {Schema} schema
 *
 * @return {boolean}
 */
export const isDataModified = ( dataContext : string, schema : Schema ) : boolean => {
    let retVal = false;

    if ( global.data[ dataContext ] && global.data[ dataContext ].schema === schema ) {
        // check item data if
        if ( !retVal && Object.keys( global.data[ dataContext ].items ).length ) {
            const items = Object.values( global.data[ dataContext ].items ) as Item[];

            for ( const item of items ) {
                if ( item.isNew() || item.isModified() ) {
                    retVal = true;
                    break;
                }
            }
        }

        // check reference data if
        if ( !retVal && Object.keys( global.data[ dataContext ].references ).length ) {
            const references = Object.values( global.data[ dataContext ].references ) as Reference[];

            for ( const reference of references ) {
                if ( reference.isNew() || reference.isModified() ) {
                    retVal = true;
                    break;
                }
            }
        }
    }

    return retVal;
}

/**
 * Is recursive data from global.data starting with rootItemId modified.
 *
 * @param {string} dataContext
 * @param {Schema} schema
 * @param {number} rootItemId
 * @param {boolean} [stopOnDeleted=true]
 *
 * @return {boolean}
 */
export const isDataModifiedRecursive = ( dataContext : string, schema : Schema, rootItemId : number, stopOnDeleted : boolean = true ) : boolean => {
    let retVal = false;

    if ( global.data[ dataContext ] && global.data[ dataContext ].schema === schema ) {
        const recursiveData = dataUtils.getReferencedData( dataContext, rootItemId, stopOnDeleted );

        // check item data
        for ( const item of recursiveData.items ) {
            if ( item.isNew() || item.isModified() ) {
                retVal = true;
                break;
            }
        }

        // check reference data if
        if ( !retVal ) {
            for ( const reference of recursiveData.references ) {
                if ( reference.isNew() || reference.isModified() ) {
                    retVal = true;
                    break;
                }
            }
        }
    }

    return retVal;
}

/**
 * Gets the modified data objects (items and references) from global.data.
 *
 * @param {string} dataContext
 * @param {Schema} schema
 *
 * @return {ItemsAndReferencesCollection}
 */
export const getModifiedData = ( dataContext : string, schema : Schema ) : ItemsAndReferencesCollection => {
    const retVal : ItemsAndReferencesCollection = {
        items     : [],
        references: []
    };

    if ( global.data[ dataContext ] && global.data[ dataContext ].schema === schema ) {
        // collect item data if
        if ( Object.keys( global.data[ dataContext ].items ).length ) {
            const items = Object.values( global.data[ dataContext ].items ) as Item[];

            for ( const item of items ) {
                if ( item.isNew() || item.isModified() ) {
                    retVal.items.push( item );
                }
            }
        }

        // collect reference data if
        if ( Object.keys( global.data[ dataContext ].references ).length ) {
            const references = Object.values( global.data[ dataContext ].references ) as Reference[];

            for ( const reference of references ) {
                if ( reference.isNew() || reference.isModified() ) {
                    retVal.references.push( reference );
                }
            }
        }
    }

    return retVal;
}

/**
 * Gets data objects (items and references) recursive from global.data starting with rootItemId.
 *
 * @param {string} dataContext
 * @param {Schema} schema
 * @param {number} rootItemId
 * @param {boolean} [stopOnDeleted=true]
 * @param {boolean} [modifiedOnly=false]
 *
 * @return {ItemsAndReferencesCollection}
 */
export const getDataRecursive = ( dataContext : string, schema : Schema, rootItemId : number, stopOnDeleted : boolean = true, modifiedOnly : boolean = false ) : ItemsAndReferencesCollection => {
    const retVal : ItemsAndReferencesCollection = {
        items     : [],
        references: []
    };

    if ( global.data[ dataContext ] && global.data[ dataContext ].schema === schema ) {
        const recursiveData = dataUtils.getReferencedData( dataContext, rootItemId, stopOnDeleted );

        // collect item data
        for ( const item of recursiveData.items ) {
            if ( !modifiedOnly || item.isNew() || item.isModified() ) {
                retVal.items.push( item );
            }
        }

        // collect reference data
        for ( const reference of recursiveData.references ) {
            if ( !modifiedOnly || reference.isNew() || reference.isModified() ) {
                retVal.references.push( reference );
            }
        }
    }

    return retVal;
}

/**
 * Gets the modified data objects (items and references) recursive from global.data starting with rootItemId.
 *
 * @param {string} dataContext
 * @param {Schema} schema
 * @param {number} rootItemId
 * @param {boolean} [stopOnDeleted=true]
 *
 * @return {ItemsAndReferencesCollection}
 */
export const getModifiedDataRecursive = ( dataContext : string, schema : Schema, rootItemId : number, stopOnDeleted : boolean = true ) : ItemsAndReferencesCollection => {
    return getDataRecursive( dataContext, schema, rootItemId, stopOnDeleted, true );
}

export type KeyUniqueCheckResult = {
    keyIsUnique : true;
    errorMessage? : never;
} | {
    keyIsUnique : false;
    errorMessage : string;
};

/**
 * Check key uniqueness with data frontend. Delete items are ignored.
 *
 * @param {string} dataContext
 * @param {number} itemTypeId
 * @param {string} key
 * @param {number} [excludeItemId]
 *
 * @return {KeyUniqueCheckResult}
 */
export const checkKeyUniqueFeOnly = ( dataContext : string, itemTypeId : number, key : string, excludeItemId? : number ) : KeyUniqueCheckResult => {
    const items   = global.data[ dataContext ]?.items ?? {},
          itemIds = Object.keys( items ) as number[];

    let retVal : KeyUniqueCheckResult = {
        keyIsUnique: true
    };

    for ( const itemId of itemIds ) {
        if ( Number( itemId ) !== excludeItemId && items[ itemId ].itemTypeId === itemTypeId && items[ itemId ].key === key && items[ itemId ].status.name !== 'deleted' ) {
            retVal = {
                keyIsUnique : false,
                errorMessage: __( 'YN.base.form.item.key.error.notUnique' )
            };
            break;
        }
    }

    return retVal;
}

export type CardinalityCheckResult = {
    cardinalityCheck : boolean;
    cardinalityError : string;
    name? : string;
    placeholders? : Placeholders;
};

/**
 * Check cardinality with data in backend and frontend. Delete references are considered.
 *
 * @param uid
 * @param dataContext
 * @param schema
 * @param referenceSetId
 * @param srcItem
 * @param destItem
 *
 * @return {Promise<CardinalityCheckResult>}
 */
export const checkCardinality = async ( uid : string, dataContext : string, schema : Schema, referenceSetId : number, srcItem? : Item, destItem? : Item ) : Promise<CardinalityCheckResult> => {
    let retVal : CardinalityCheckResult,
        referencesToSrcItem : number[]  = [],
        referencesToDestItem : number[] = [];

    const requestUid  = 'checkCardinality',
          state       = store.getState(),
          cardinality = metaModel[ schema ].getReferenceSet( referenceSetId )?.cardinality ?? '',
          limitLeft   = cardinality?.startsWith( '1:' ) ?? false,
          limitRight  = cardinality?.endsWith( ':1' ) ?? false;

    // abort running check
    if ( state.xhr?.[ uid ]?.[ requestUid ] ) {
        xhr.abort( uid, requestUid );
    }

    try {
        // if cardinality is n:m, there is nothing to check
        if ( cardinality === 'n:m' ) {
            retVal = {
                cardinalityCheck: true,
                cardinalityError: ''
            };
        }
        else {
            // if at least one side is an existing item and the other side is limited to 1, count the existing references to the existing item in database (backend)
            if ( ( ( srcItem?.id ?? 0 ) > 0 && limitRight ) || ( ( destItem?.id ?? 0 > 0 ) && limitLeft ) ) {

                // get matching reference ids from backend
                const params : GetReferenceIdsParameters = {
                          schema        : schema,
                          referenceSetId: referenceSetId,
                          srcItemId     : ( srcItem?.id ?? 0 ) > 0 && limitRight ? srcItem?.id : undefined,
                          destItemId    : ( destItem?.id ?? 0 > 0 ) && limitLeft ? destItem?.id : undefined
                      },
                      response                           = await xhr.post(
                          `${BASE_URL}/api/data/getReferenceIds`,
                          params,
                          undefined,
                          uid,
                          requestUid
                      );

                if ( response?.data ) {
                    const responseData = response.data as GetReferenceIdsResult;

                    if ( responseData.success ) {
                        referencesToSrcItem = responseData.data.referencesToSrcItem ?? [];
                        referencesToDestItem = responseData.data.referencesToDestItem ?? [];
                    }
                }
            }

            // get matching reference ids hold in datContext on frontend, merge them with references from backend and filter deleted references out
            const referencesToSrcItemInFE = srcItem?.id ? dataUtilities.findReferences( dataContext, referenceSetId, srcItem.id ) ?? [] : [];
            referencesToSrcItem = _.uniq( referencesToSrcItemInFE.filter( reference => reference.status.name !== 'deleted' )
                                                                 .map( reference => reference.id )
                                                                 .concat( referencesToSrcItem.filter( referenceId => global.data[ dataContext ].references[ referenceId ]?.status.name !== 'deleted' ) ) );

            const referencesToDestItemInFE = destItem?.id ? dataUtilities.findReferences( dataContext, referenceSetId, destItem.id ) ?? [] : [];
            referencesToDestItem = _.uniq( referencesToDestItemInFE.filter( reference => reference.status.name !== 'deleted' )
                                                                   .map( reference => reference.id )
                                                                   .concat( referencesToDestItem.filter( referenceId => global.data[ dataContext ].references[ referenceId ]?.status.name !== 'deleted' ) ) );

            // cardinality check is ok, if no limit or no reference exists
            const cardinalityCheck = ( !limitRight || !referencesToSrcItem.length ) && ( !limitLeft || !referencesToDestItem.length );

            retVal = {
                cardinalityCheck,
                cardinalityError: cardinalityCheck ? '' : new LocalizableError( 'YN.base.finder.reference.check.insertable.cardinality.violation', { cardinality: cardinality } ).toString( false )
            }
        }

    }
    catch ( error ) {
        retVal = {
            cardinalityCheck: false,
            cardinalityError: error as string
        };
    }

    return retVal;
}

/**
 * Check cardinality with data in backend and frontend. Delete references are considered.
 *
 * @param uid
 * @param schema
 * @param referenceSetId
 * @param srcItem
 * @param destItem
 * @param excludeReferenceId
 *
 * @return { cardinalityCheck : boolean, cardinalityError : string }
 */
export const checkCardinalityFeOnly = ( dataContext : string, schema : Schema, referenceSetId : number, srcItem? : Item, destItem? : Item, excludeReferenceId? : number ) : CardinalityCheckResult => {
    let retVal : CardinalityCheckResult,
        referencesToSrcItem : number[]  = [],
        referencesToDestItem : number[] = [];

    const cardinality = metaModel[ schema ].getReferenceSet( referenceSetId )?.cardinality ?? '',
          limitLeft   = cardinality?.startsWith( '1:' ) ?? false,
          limitRight  = cardinality?.endsWith( ':1' ) ?? false;

    try {
        // if cardinality is n:m, there is nothing to check
        if ( cardinality === 'n:m' ) {
            retVal = {
                cardinalityCheck: true,
                cardinalityError: ''
            };
        }
        else {
            // get matching reference ids hold in datContext on frontend, merge them with references from backend and filter deleted references out
            const referencesToSrcItemInFE = srcItem?.id ? dataUtilities.findReferences( dataContext, referenceSetId, srcItem.id, undefined, true ) ?? [] : [];
            referencesToSrcItem = _.uniq( referencesToSrcItemInFE.filter( reference => reference.status.name !== 'deleted' && ( !excludeReferenceId || reference.id !== excludeReferenceId ) )
                                                                 .map( reference => reference.id ) );

            const referencesToDestItemInFE = destItem?.id ? dataUtilities.findReferences( dataContext, referenceSetId, undefined, destItem.id, true ) ?? [] : [];
            referencesToDestItem = _.uniq( referencesToDestItemInFE.filter( reference => reference.status.name !== 'deleted' && ( !excludeReferenceId || reference.id !== excludeReferenceId ) )
                                                                   .map( reference => reference.id ) );

            // cardinality check is ok, if no limit or no reference exists
            const cardinalityCheck = ( !limitRight || !referencesToSrcItem.length ) && ( !limitLeft || !referencesToDestItem.length );

            retVal = {
                cardinalityCheck,
                cardinalityError: cardinalityCheck ? '' : new LocalizableError( 'YN.base.finder.reference.check.insertable.cardinality.violation', { cardinality: cardinality } ).toString( false ),
                name            : 'YN.base.finder.reference.check.insertable.cardinality.violation',
                placeholders    : { cardinality: cardinality }
            };
        }

    }
    catch ( error ) {
        retVal = {
            cardinalityCheck: false,
            cardinalityError: error as string
        };
    }

    return retVal;
}

const filterServiceFilters = Object.keys( FilterService.filters ) as ConditionOperator[];

/**
 * Resolves the condition recursively.
 *
 * @param {Style} condition
 * @param {DataDescriptorValues} values
 *
 * @return {boolean}
 */
export const resolveConditionRecursive = ( condition : Condition, values : DataDescriptorValues ) : boolean => {
    let retVal = false;

    if ( condition.leftExpression !== undefined || condition.leftExpressionDataDescriptor !== undefined ) {

        if ( ( condition.operator === 'and' || condition.operator === 'or' ) && Array.isArray( condition.leftExpression ) ) {
            retVal = condition.leftExpression.reduce(
                ( innerResult : boolean, subCondition : any ) => {
                    if ( condition.operator === 'and' ) {
                        return innerResult && resolveConditionRecursive( subCondition, values );
                    }
                    else {
                        return innerResult || resolveConditionRecursive( subCondition, values );
                    }
                },
                condition.operator === 'and'
            );
        }

        else {
            const valueResolver = messageUtils.buildReturnFunction( values );

            let operator : ConditionOperator | undefined,
                operatorNegation : boolean = false;

            if ( condition.operator === 'notStartsWith' ) {
                operator = 'startsWith';
                operatorNegation = true;
            }
            else if ( condition.operator === 'notContains' ) {
                operator = 'contains';
                operatorNegation = true;
            }
            else if ( condition.operator === 'notEndsWith' ) {
                operator = 'endsWith';
                operatorNegation = true;
            }
            else if ( condition.operator === 'notIn' ) {
                operator = 'in';
                operatorNegation = true;
            }
            else if ( condition.operator === 'notEmpty' ) {
                operator = 'empty';
                operatorNegation = true;
            }
            else if ( condition.operator === 'notEquals' ) {
                operator = 'equals';
                operatorNegation = true;
            }
            else {
                operator = condition.operator;
            }

            // TODO: cast left and right value to same type

            if ( operator && ( filterServiceFilters.includes( operator ) || operator === 'empty' ) ) {
                let leftValue = condition.leftExpression !== undefined && typeof condition.leftExpression === 'string'
                    ? condition.leftExpression.match( /(?<=\$\{?)([\w\p{L}\p{N}]|\.)+(?=\}|[^\w\p{L}\p{N}]|$)/gimu )
                        ? __( valueResolver( condition.leftExpression ), values )
                        : values[ condition.leftExpression ] !== undefined
                            ? values[ condition.leftExpression ]
                            : condition.leftExpression
                    : condition.leftExpression;

                if ( leftValue === undefined && condition.leftExpressionDataDescriptor ) {
                    leftValue = values[ condition.leftExpressionDataDescriptor.alias ];
                }

                if ( operator === 'empty' ) {
                    retVal = leftValue === undefined || leftValue === null || leftValue === '';
                }

                else if ( operator === 'in' ) {
                    if ( Array.isArray( condition.rightExpression ) ) {
                        const rightValue = condition.rightExpression.map( rEC => {
                            let innerRetVal = rEC.leftExpression;

                            if ( innerRetVal === undefined && rEC.leftExpressionDataDescriptor ) {
                                innerRetVal = values[ rEC.leftExpressionDataDescriptor.alias ];
                            }

                            return innerRetVal;
                        } );

                        retVal = rightValue.includes( leftValue );
                    }
                }

                else {
                    let rightValue = condition.rightExpression !== undefined && typeof condition.rightExpression === 'string'
                        ? condition.rightExpression.match( /(?<=\$\{?)([\w\p{L}\p{N}]|\.)+(?=\}|[^\w\p{L}\p{N}]|$)/gimu )
                            ? __( valueResolver( condition.rightExpression ), values )
                            : values[ condition.rightExpression ] !== undefined
                                ? values[ condition.rightExpression ]
                                : condition.rightExpression
                        : condition.rightExpression;

                    if ( rightValue === undefined && condition.rightExpressionDataDescriptor ) {
                        rightValue = values[ condition.rightExpressionDataDescriptor.alias ];
                    }

                    // interface filters described for namespace FilterService so no idea...
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    retVal = FilterService.filters[ operator ]( leftValue, rightValue );
                }

                if ( operatorNegation ) {
                    retVal = !retVal;
                }
            }
        }
    }

    return retVal;
}

export type Value = {
    value : any;
    valueType : FilterValueType;

    defaultValue? : any;
    statusId? : number;
    isEditable? : boolean;
    isDefault? : boolean;
    isNew? : boolean;
    isModified? : boolean;
    isDeleted? : boolean;
    valueSetId? : number;
    valueUnit? : string;
};

export const getValueForDataDescriptorFromRowData = ( dataContext : string, dataDescriptor : DataDescriptor, rowData : LoadMetaDataRowData, dataStructure : DataStructure, columnField : string = '', rawMode : boolean = false, showAdditionalRawNames : boolean = false, showPropertyDefaults : boolean = true ) : Value | undefined => {
    let retVal : Value | undefined;

    if ( Object.keys( dataStructure ).length && dataDescriptor ) {
        let schema                  = ( dataDescriptor.dataObject?.item.objectSchema ?? global.data[ dataContext ]?.schema ?? 'DATA' ).toUpperCase() as Schema,
            value : any | undefined = rowData[ columnField ? columnField : dataDescriptor.alias ],
            defaultValue : any | undefined,
            statusId : number | undefined,
            isDefault : boolean     = false,
            isNew : boolean         = false,
            isEditable : boolean    = false,
            isModified : boolean    = false,
            isDeleted : boolean     = false,
            valueSetId : number | undefined,
            valueUnit : string | undefined;

        if ( dataDescriptor.type === 'item' ) {
            const item = getItemForDataDescriptorFromRowData( dataContext, dataDescriptor, rowData );

            if ( item ) {
                schema = item.schema;

                statusId = item.statusId;
                isNew = item.isNew();
                isDeleted = item.isDeleted();
                isEditable = !item.isDeleted();

                switch ( dataDescriptor.attribute ) {
                    case 'itemTypeName':
                        value = getItemTypeDisplayName( item.itemTypeId, schema, rawMode, showAdditionalRawNames );
                        break;

                    case  'statusName':
                        value = statusRenderer( item.statusId, schema, rawMode, showAdditionalRawNames );
                        break;

                    default:
                        value = item[ dataDescriptor.attribute ];
                        isModified = item.isAttributeModified( dataDescriptor.attribute as keyof ItemOnlyAttributesSettable );
                }

                if ( isDeleted ) {
                    isModified = item.isModified();
                }
            }
        }

        else if ( dataDescriptor.type === 'property' ) {
            const property = getPropertyForDataDescriptorFromRowData( dataContext, dataDescriptor, rowData );

            if ( property ) {
                schema = property.schema;

                statusId = property.statusId;
                isNew = property.isNew();
                isDeleted = property.isDeleted();
                isEditable = !property.isDeleted();

                switch ( dataDescriptor.attribute ) {
                    case 'propertyTypeName':
                        value = getPropertyTypeDisplayName( property.propertySet.propertyTypeId, schema, rawMode, showAdditionalRawNames );
                        break;

                    case 'propertySetName':
                        value = getPropertySetDisplayName( property.propertySetId, schema, rawMode, showAdditionalRawNames );
                        break;

                    case  'statusName':
                        value = statusRenderer( property.statusId, schema, rawMode, showAdditionalRawNames );
                        break;

                    default:
                        value = property[ dataDescriptor.attribute as keyof Property ];
                        isModified = property.isAttributeModified( dataDescriptor.attribute as keyof PropertyAttributesSettable );
                }

                if ( isDeleted ) {
                    isModified = property.isModified();
                }
            }

            else {
                const item = getItemForDataDescriptorFromRowData( dataContext, dataDescriptor, rowData );

                if ( item ) {
                    isEditable = !item.isDeleted();
                }
            }

            // value specials
            if ( dataDescriptor.attribute === 'value' ) {
                //  handle defaultValue if showPropertyDefaults
                if ( !property && ( !dataDescriptor.aggregate || showPropertyDefaults ) ) {
                    const propertySet = dataDescriptor.dataObject.item.objectType
                        ? metaModel[ schema ].getPropertySetForItemTypeAndPropertyType( dataDescriptor.dataObject.item.objectType, dataDescriptor.propertyTypeName )
                        : undefined;

                    // set defaultValue if
                    if ( !dataDescriptor.aggregate && propertySet?.valueDefault !== undefined && propertySet?.valueDefault !== '' ) {
                        defaultValue = propertySet.valueDefault;
                        if ( showPropertyDefaults ) {
                            isDefault = true;
                            value = propertySet.valueDefault;
                        }
                    }

                    //  set valueSetId if
                    if ( propertySet?.valueSetId ) {
                        valueSetId = propertySet.valueSetId;
                    }

                    //  add unit to renderValueParams if
                    if ( propertySet?.valueUnit ) {
                        valueUnit = propertySet.valueUnit;
                    }
                }

                // set defaultValue if
                if ( property?.propertySet.valueDefault !== undefined && property?.propertySet.valueDefault !== '' ) {
                    defaultValue = property.propertySet.valueDefault;
                }

                //  set valueSetId if
                if ( property?.propertySet.valueSetId ) {
                    valueSetId = property.propertySet.valueSetId;
                }

                //  add unit to renderValueParams if
                if ( property?.propertySet.valueUnit ) {
                    valueUnit = property.propertySet.valueUnit;
                }
            }
        }

        else if ( dataDescriptor.type === 'reference' ) {
            const reference = getReferenceForDataDescriptorFromRowData( dataContext, dataDescriptor, rowData );

            if ( reference ) {
                schema = reference.schema;

                statusId = reference.statusId;
                isNew = reference.isNew();
                isDeleted = reference.isDeleted();
                // check for the real referenceSet (each row could contain a different one)
                isEditable = user!.hasPermissionOnReferenceSet( 'update', reference.referenceSetId, schema ) && !reference.isDeleted();

                let dir : string = '';

                switch ( dataDescriptor.attribute ) {
                    case 'referenceEnd':
                        if ( reference.srcItemId === reference.destItemId ) {
                            value = 'both';
                        }
                        else if ( reference.srcItemId === Number( value ) ) {
                            value = 'src';
                        }
                        else {
                            value = 'dest';
                        }
                        break;

                    case 'referenceTypeName':
                        value = getReferenceTypeDisplayName( reference.referenceSetId, schema, rawMode, showAdditionalRawNames );
                        break;

                    case 'referenceTypeNameWithDirection':
                        if ( reference.srcItemId === reference.destItemId ) {
                            dir = 'both';
                        }
                        else if ( reference.srcItemId === Number( value ) ) {
                            dir = 'src';
                        }
                        else {
                            dir = 'dest';
                        }

                        value = dir + '::' + getReferenceTypeDisplayName( reference.referenceSetId, schema, rawMode, showAdditionalRawNames );
                        break;

                    case 'referenceSetName':
                        value = getReferenceSetDisplayName( reference.referenceSetId, schema, rawMode, showAdditionalRawNames );
                        break;

                    case  'statusName':
                        value = statusRenderer( reference.statusId, schema, rawMode, showAdditionalRawNames );
                        break;

                    default:
                        value = reference[ dataDescriptor.attribute ];
                        isModified = reference.isAttributeModified( dataDescriptor.attribute as keyof ReferenceOnlyAttributesSettable );
                }

                if ( isDeleted ) {
                    isModified = reference.isModified();
                }
            }
        }

        else if ( dataDescriptor.type === 'referenceProperty' ) {
            const referenceProperty = getReferencePropertyForDataDescriptorFromRowData( dataContext, dataDescriptor, rowData );

            if ( referenceProperty ) {
                schema = referenceProperty.schema;

                statusId = referenceProperty.statusId;
                isNew = referenceProperty.isNew();
                isDeleted = referenceProperty.isDeleted();
                // check for the real referencePropertySet (each row could contain a different one)
                isEditable = user!.hasPermissionOnReferencePropertySet( 'update', referenceProperty.referencePropertySetId, schema ) && !referenceProperty.isDeleted();

                switch ( dataDescriptor.attribute ) {
                    case 'propertyTypeName':
                        value = getPropertyTypeDisplayName( referenceProperty.referencePropertySet.propertyTypeId, schema, rawMode, showAdditionalRawNames );
                        break;

                    case 'referencePropertySetName':
                        value = getReferencePropertySetDisplayName( referenceProperty.referencePropertySetId, schema, rawMode, showAdditionalRawNames );
                        break;

                    case  'statusName':
                        value = statusRenderer( referenceProperty.statusId, schema, rawMode, showAdditionalRawNames );
                        break;

                    case undefined:
                        value = undefined;
                        break;

                    default:
                        value = referenceProperty[ dataDescriptor.attribute as keyof ReferenceProperty ];
                        isModified = referenceProperty.isAttributeModified( dataDescriptor.attribute as keyof ReferencePropertyAttributesSettable );
                }

                if ( isDeleted ) {
                    isModified = referenceProperty.isModified();
                }
            }

            else {
                const reference = getReferenceForDataDescriptorFromRowData( dataContext, dataDescriptor, rowData );

                if ( reference ) {
                    isEditable = user!.hasPermissionOnReferenceSet( 'update', reference.referenceSetId, schema ) && !reference.isDeleted();
                }
            }

            // value specials
            if ( dataDescriptor.attribute === 'value' ) {
                //  handle defaultValue if showPropertyDefaults
                if ( !referenceProperty && ( !dataDescriptor.aggregate || showPropertyDefaults ) ) {
                    const reference            = getReferenceForDataDescriptorFromRowData( dataContext, dataDescriptor, rowData ),
                          referencePropertySet = reference
                              ? metaModel[ schema ].getReferencePropertySetForReferenceSetAndPropertyType( reference.referenceSetId, dataDescriptor.propertyTypeName )
                              : undefined;

                    if ( !dataDescriptor.aggregate && referencePropertySet?.valueDefault !== undefined && referencePropertySet?.valueDefault !== '' ) {
                        defaultValue = referencePropertySet.valueDefault;
                        if ( showPropertyDefaults ) {
                            isDefault = true;
                            value = referencePropertySet.valueDefault;
                        }
                    }

                    //  set valueSetId if
                    if ( referencePropertySet?.valueSetId ) {
                        valueSetId = referencePropertySet.valueSetId;
                    }

                    //  add unit to renderValueParams if
                    if ( referencePropertySet?.valueUnit ) {
                        valueUnit = referencePropertySet.valueUnit;
                    }
                }

                // set defaultValue if
                if ( referenceProperty?.referencePropertySet.valueDefault !== undefined && referenceProperty?.referencePropertySet.valueDefault !== '' ) {
                    defaultValue = referenceProperty.referencePropertySet.valueDefault;
                }

                //  set valueSetId if
                if ( referenceProperty?.referencePropertySet.valueSetId ) {
                    valueSetId = referenceProperty.referencePropertySet.valueSetId;
                }

                //  add unit to renderValueParams if
                if ( referenceProperty?.referencePropertySet.valueUnit ) {
                    valueUnit = referenceProperty.referencePropertySet.valueUnit;
                }
            }
        }

        // TODO: implement if needed
        //  merge valueUnits and valuePrecisions -> see shared/src/dataUtils:getDataValueType for further information
        //else if ( dataDescriptor.type === 'expression' ) {}

        retVal = {
            value,
            defaultValue,
            valueType : dataUtils.getDataValueType( dataDescriptor, schema, dataStructure ) ?? 'STRING',
            statusId,
            isEditable,
            isDefault,
            isNew,
            isModified: isModified,
            isDeleted,
            valueSetId,
            valueUnit
        };
    }

    return retVal;
};

/**
 * Get the item described by dataDescriptor and addressed by rowData.
 *
 * @param {string} dataContext
 * @param {DataDescriptor} dataDescriptor
 * @param {LoadMetaDataRowData} rowData
 *
 * @return {Item | undefined}
 */
export const getItemForDataDescriptorFromRowData = ( dataContext : string, dataDescriptor : DataDescriptor, rowData : LoadMetaDataRowData ) : Item | undefined => {
    let retVal : Item | undefined;

    if ( dataDescriptor.type === 'item' || dataDescriptor.type === 'property' ) {
        const itemIdDataDescriptor = buildDataDescriptor( { attributePath: 'item.id' }, dataDescriptor.dataObject ) as ItemDataDescriptor,
              itemId               = rowData[ itemIdDataDescriptor?.alias ];

        retVal = itemId ? global.data[ dataContext ]?.items?.[ itemId ] : undefined;
    }

    return retVal;
};

/**
 * Get the property described by dataDescriptor and addressed by rowData.
 *
 * @param {string} dataContext
 * @param {DataDescriptor} dataDescriptor
 * @param {LoadMetaDataRowData} rowData
 *
 * @return {Property | undefined}
 */
export const getPropertyForDataDescriptorFromRowData = ( dataContext : string, dataDescriptor : DataDescriptor, rowData : LoadMetaDataRowData ) : Property | undefined => {
    let retVal : Property | undefined;

    if ( dataDescriptor.type === 'property' ) {
        const schema               = ( dataDescriptor.dataObject.item.objectSchema ?? global.data[ dataContext ]?.schema ?? 'DATA' ).toUpperCase() as Schema,
              itemIdDataDescriptor = buildDataDescriptor( { attributePath: 'item.id' }, dataDescriptor.dataObject ) as ItemDataDescriptor,
              itemId               = rowData[ itemIdDataDescriptor?.alias ],
              item                 = itemId ? global.data[ dataContext ]?.items?.[ itemId ] : undefined;

        if ( item ) {
            const propertySet = metaModel[ schema ].getPropertySetForItemTypeAndPropertyType( item.itemTypeId, dataDescriptor.propertyTypeName );

            if ( propertySet ) {
                if ( propertySet.isEnumerated ) {
                    const propertyIdDataDescriptor = buildDataDescriptor( { attributePath: `property.${dataDescriptor.propertyTypeName}.id` }, dataDescriptor.dataObject ) as ItemDataDescriptor,
                          propertyId               = rowData[ propertyIdDataDescriptor?.alias ],
                          enumProperty             = item.enumProperties.get( propertySet.propertyTypeId );

                    if ( propertyId && enumProperty ) {
                        enumProperty.properties.forEach( prop => {
                            if ( prop.id === propertyId ) {
                                retVal = prop;
                            }
                        } );
                    }
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                    else if ( enumProperty?.isNew() || enumProperty?.properties.size === 1 ) {
                        retVal = [ ...enumProperty.properties ][ 0 ][ 1 ];
                    }
                }
                else {
                    retVal = item.properties.get( propertySet.propertyTypeId );
                }
            }
        }
    }

    return retVal;
};

/**
 * Get the reference described by dataDescriptor and addressed by rowData.
 *
 * @param {string} dataContext
 * @param {DataDescriptor} dataDescriptor
 * @param {LoadMetaDataRowData} rowData
 *
 * @return {Reference | undefined}
 */
export const getReferenceForDataDescriptorFromRowData = ( dataContext : string, dataDescriptor : DataDescriptor, rowData : LoadMetaDataRowData ) : Reference | undefined => {
    let retVal : Reference | undefined;

    if ( dataDescriptor.type === 'reference' || dataDescriptor.type === 'referenceProperty' ) {
        const referenceIdDataDescriptor = buildDataDescriptor( { attributePath: 'reference.id' }, dataDescriptor.dataObject ) as ReferenceDataDescriptor,
              referenceId               = rowData[ referenceIdDataDescriptor?.alias ];

        retVal = referenceId ? global.data[ dataContext ]?.references?.[ referenceId ] : undefined;
    }

    return retVal;
};

/**
 * Get the referenceProperty described by dataDescriptor and addressed by rowData.
 *
 * @param {string} dataContext
 * @param {DataDescriptor} dataDescriptor
 * @param {LoadMetaDataRowData} rowData
 *
 * @return {ReferenceProperty | undefined}
 */
export const getReferencePropertyForDataDescriptorFromRowData = ( dataContext : string, dataDescriptor : DataDescriptor, rowData : LoadMetaDataRowData ) : ReferenceProperty | undefined => {
    let retVal : ReferenceProperty | undefined;

    if ( dataDescriptor.type === 'referenceProperty' ) {
        const schema                    = ( dataDescriptor.dataObject.item.objectSchema ?? global.data[ dataContext ]?.schema ?? 'DATA' ).toUpperCase() as Schema,
              referenceIdDataDescriptor = buildDataDescriptor( { attributePath: 'reference.id' }, dataDescriptor.dataObject ) as ReferenceDataDescriptor,
              referenceId               = rowData[ referenceIdDataDescriptor?.alias ],
              reference                 = referenceId ? global.data[ dataContext ]?.references?.[ referenceId ] : undefined;

        if ( reference ) {
            const referencePropertySet = metaModel[ schema ].getReferencePropertySetForReferenceSetAndPropertyType( reference.referenceSetId, dataDescriptor.propertyTypeName );

            if ( referencePropertySet ) {
                if ( referencePropertySet.isEnumerated ) {
                    const referencePropertyIdDataDescriptor = buildDataDescriptor( { attributePath: `property.${dataDescriptor.propertyTypeName}.id` }, dataDescriptor.dataObject ) as ReferencePropertyDataDescriptor,
                          referencePropertyId               = rowData[ referencePropertyIdDataDescriptor?.alias ],
                          enumReferenceProperty             = reference.enumProperties.get( referencePropertySet.propertyTypeId );

                    if ( referencePropertyId && enumReferenceProperty ) {
                        enumReferenceProperty.properties.forEach( prop => {
                            if ( prop.id === referencePropertyId ) {
                                retVal = prop;
                            }
                        } );
                    }
                    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                    else if ( enumReferenceProperty?.isNew() || enumReferenceProperty?.properties.size === 1 ) {
                        retVal = [ ...enumReferenceProperty.properties ][ 0 ][ 1 ];
                    }
                }
                else {
                    retVal = reference.properties.get( referencePropertySet.propertyTypeId );
                }
            }
        }
    }

    return retVal;
};
